/*
 This file is part of GNU Taler
 (C) 2025 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU Affero Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU Affero Public License for more details.

 You should have received a copy of the GNU Affero Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import { TalerFormAttributes } from "@gnu-taler/taler-util";
import { format, intervalToDuration, isFuture, isValid, parse } from "date-fns";
import { InternationalizationAPI } from "../../context/translation.js";
import { DoubleColumnFormDesign, UIFormElementConfig } from "../forms-types.js";
import { countryNationalityList } from "../../utils/select-ui-lists.js";
import { vqfLanguagePolicy } from "./vqf.js";

export const Descr = {
  CUSTOMER_INFO_TYPE: (i18n: InternationalizationAPI) =>
    i18n.fixed({
      en: "The customer is the person with whom the member concludes the contract with regard to the financial service provided (civil law). If the\n  member acts as the director of a domiciliary company, then that domiciliary company is the customer.",
      de: "Kunde ist die Person, mit welcher das Mitglied den Vertrag über die erbrachte Finanzdienstleistung abschliesst (Zivilrecht). Handelt das\n  Mitglied als Organ einer Sitzgesellschaft, so ist diese Sitzgesellschaft der Kunde.",
    }),
} as const;

export const form_vqf_902_1_customer = (i18n: InternationalizationAPI) => ({
  label: i18n.fixed({
    en: "Identification Form (customer)",
    de: "Identifikationsformular (Kunde)",
  }),
  description: i18n.fixed({
    en: "The customer has to be identified on entering into a permanent business relationship or on concluding a cash transaction, which meets the according threshold.",
    de: "Die Vertragspartei muss identifiziert werden bei allen dauernden Geschäftsbeziehungen sowie bei Kassageschäften, bei welchen der entsprechende Schwellenwert erreicht wird.",
  }),
  id: "vqf_902_1_customer",
  version: 1,
  config: design_VQF_902_1_customer(i18n),
});

const fieldCorrespondenceLanguage = (
  i18n: InternationalizationAPI,
): UIFormElementConfig => ({
  id: TalerFormAttributes.CORRESPONDENCE_LANGUAGE,
  required: true,
  label: i18n.fixed({
    en: "Correspondence language:",
    de: "Korrespondenzsprache:",
  }),
  type: "choiceStacked",
  choices: [
    {
      value: "en",
      label: i18n.fixed({ en: "English", de: "Englisch" }),
    },
    {
      value: "de",
      label: i18n.fixed({ en: "German", de: "Deutsch" }),
    },
    {
      value: "fr",
      label: i18n.fixed({ en: "French", de: "Französisch" }),
    },
    {
      value: "it",
      label: i18n.fixed({ en: "Italian", de: "Italienisch" }),
    },
  ],
});

/**
 * Form vqf_902_1_customer.
 */
export function design_VQF_902_1_customer(
  i18n: InternationalizationAPI,
): DoubleColumnFormDesign {
  const today = format(new Date(), "yyyy-MM-dd");

  return {
    type: "double-column",
    languagePolicy: vqfLanguagePolicy(i18n),
    title: i18n.fixed({
      en: "Identification form (basic customer information)",
      de: "Identifikationsformular (Basisangaben zum Kunden)",
    }),
    sections: [
      {
        title: i18n.fixed({
          en: "Information on customer",
          de: "Angaben zum Kunden",
        }),
        description: Descr.CUSTOMER_INFO_TYPE(i18n),
        fields: [
          {
            id: TalerFormAttributes.CUSTOMER_TYPE,
            label: i18n.fixed({ en: "Customer type", de: "Kundentyp" }),
            type: "choiceStacked",
            choices: [
              {
                value: "NATURAL_PERSON",
                label: i18n.fixed({
                  en: "Natural person (incl. sole proprietors)",
                  de: "Natürliche Person (inkl. Einzelunternehmen)",
                }),
              },
              {
                value: "LEGAL_ENTITY",
                label: i18n.fixed({
                  en: "Legal entity",
                  de: "Juristische Person",
                }),
              },
            ],
            required: true,
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Information on customer",
          de: "Angaben zum Kunden",
        }),
        description: i18n.fixed({
          en: "Applicable if customer is a natural person",
          de: "Anwendbar, wenn der Kunde eine natürliche Person ist",
        }),
        hide(root) {
          return root[TalerFormAttributes.CUSTOMER_TYPE] !== "NATURAL_PERSON";
        },
        fields: [
          {
            id: TalerFormAttributes.FULL_NAME,
            label: i18n.fixed({ en: "Full name", de: "Vollständiger Name" }),
            type: "text",
            required: true,
          },
          {
            id: TalerFormAttributes.DOMICILE_ADDRESS,
            label: i18n.fixed({
              en: "Residential address in Switzerland",
              de: "Wohnsitzadresse in der Schweiz",
            }),
            type: "textArea",
            required: true,
          },
          {
            id: TalerFormAttributes.CONTACT_PHONE,
            label: i18n.fixed({ en: "Telephone", de: "Telefon" }),
            type: "text",
            required: false,
          },
          {
            id: TalerFormAttributes.CONTACT_EMAIL,
            label: i18n.fixed({ en: "Email", de: "E-Mail" }),
            type: "text",
            required: false,
          },
          {
            id: TalerFormAttributes.DATE_OF_BIRTH,
            label: i18n.fixed({ en: "Date of birth", de: "Geburtsdatum" }),
            type: "isoDateText",
            placeholder: "dd/MM/yyyy",
            pattern: "dd/MM/yyyy",
            defaultCalendarValue: "1980-01-01",
            required: true,
            validator(text, form) {
              //FIXME: why returning in this format even if pattern is in another?
              const time = parse(text, "yyyy-MM-dd", new Date());
              if (!isValid(time)) {
                return i18n.fixed({
                  en: "invalid format",
                  de: "ungültiges Format",
                });
              }
              if (isFuture(time)) {
                return i18n.fixed({
                  en: "it can't be in the future",
                  de: "darf nicht in der Zukunft liegen",
                });
              }
              const { years } = intervalToDuration({
                start: time,
                end: new Date(),
              });
              if (years && years > 120) {
                return i18n.fixed({
                  en: "it can't be greater than 120 years",
                  de: "darf nicht größer als 120 Jahre sein",
                });
              }
              return undefined;
            },
          },
          {
            id: TalerFormAttributes.NATIONALITY,
            label: i18n.fixed({ en: "Nationality", de: "Staatsangehörigkeit" }),
            type: "selectOne",
            choices: countryNationalityList(i18n),
            preferredChoiceVals: ["CH"],
            required: true,
          },
          {
            id: TalerFormAttributes.PERSONAL_IDENTIFICATION_DOCUMENT_COPY,
            label: i18n.fixed({
              en: "Copy of identification document",
              de: "Kopie des Ausweisdokuments",
            }),
            type: "file",
            accept: "application/pdf",
            tooltip: i18n.fixed({
              en: "Only official government IDs (incl. passports) are accepted. Please scan both sides if applicable.",
              de: "Es werden nur amtliche Ausweise (inkl. Pässe) akzeptiert. Bitte scannen Sie gegebenenfalls beide Seiten.",
            }),
            required: true,
          },
          {
            id: TalerFormAttributes.CUSTOMER_IS_SOLE_PROPRIETOR,
            label: i18n.fixed({
              en: "Sole proprietor",
              de: "Einzelunternehmen",
            }),
            type: "toggle",
            threeState: false,
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Information on customer (sole proprietor)",
          de: "Angaben zum Kunden (Einzelunternehmen)",
        }),
        description: i18n.fixed({
          en: "Applicable only if customer is a sole proprietor",
          de: "Nur anwendbar, wenn der Kunde ein Einzelunternehmen ist",
        }),
        hide(root) {
          return (
            root[TalerFormAttributes.CUSTOMER_TYPE] !== "NATURAL_PERSON" ||
            !root[TalerFormAttributes.CUSTOMER_IS_SOLE_PROPRIETOR]
          );
        },
        fields: [
          {
            id: TalerFormAttributes.COMPANY_NAME,
            label: i18n.fixed({ en: "Company name", de: "Firmenname" }),
            type: "text",
            required: true,
          },
          {
            id: TalerFormAttributes.REGISTERED_OFFICE_ADDRESS,
            label: i18n.fixed({ en: "Registered office", de: "Sitz" }),
            type: "text",
            required: true,
          },
          {
            id: TalerFormAttributes.LEGAL_ENTITY_IDENTIFICATION_DOCUMENT_COPY,
            label: i18n.fixed({
              en: "Company identification document",
              de: "Identifikationsdokument des Unternehmens",
            }),
            type: "file",
            required: true,
            accept: "application/pdf",
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Information on customer (legal entity)",
          de: "Angaben zum Kunden (juristische Person)",
        }),
        description: i18n.fixed({
          en: "Applicable if customer is a legal entity",
          de: "Anwendbar, wenn der Kunde eine juristische Person ist",
        }),
        hide(root) {
          return root[TalerFormAttributes.CUSTOMER_TYPE] !== "LEGAL_ENTITY";
        },
        fields: [
          {
            id: TalerFormAttributes.COMPANY_NAME,
            label: i18n.fixed({ en: "Company name", de: "Firmenname" }),
            type: "text",
            required: true,
          },
          {
            id: TalerFormAttributes.DOMICILE_ADDRESS,
            label: i18n.fixed({ en: "Domicile", de: "Sitz" }),
            type: "textArea",
            required: true,
          },
          {
            id: TalerFormAttributes.CONTACT_PERSON_NAME,
            label: i18n.fixed({ en: "Contact person", de: "Kontaktperson" }),
            type: "text",
            required: false,
          },
          {
            id: TalerFormAttributes.CONTACT_PHONE,
            label: i18n.fixed({ en: "Telephone", de: "Telefon" }),
            type: "text",
            required: false,
          },
          {
            id: TalerFormAttributes.CONTACT_EMAIL,
            label: i18n.fixed({ en: "Email", de: "E-Mail" }),
            type: "text",
            required: false,
          },
          {
            id: TalerFormAttributes.LEGAL_ENTITY_IDENTIFICATION_DOCUMENT_COPY,
            label: i18n.fixed({
              en: "Copy of identification document (not older than 12 months)",
              de: "Kopie des Ausweisdokuments (nicht älter als 12 Monate)",
            }),
            type: "file",
            accept: "application/pdf",
            required: true,
          },
          {
            id: TalerFormAttributes.COMPANY_SHARE_REGISTRY,
            label: i18n.fixed({
              en: "Information on Ownership / Shareholding (Share register or equivalent)",
              de: "Angaben über die Beteiligungsverhältnisse (Aktienregister oder Äquivalent)",
            }),
            type: "file",
            accept: "application/pdf",
            required: false,
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Correspondence Preferences",
          de: "Korrespondenzpräferenzen",
        }),
        hide(root) {
          return !root[TalerFormAttributes.CUSTOMER_TYPE];
        },
        fields: [fieldCorrespondenceLanguage(i18n)],
      },
      {
        title: i18n.fixed({
          en: "Information on the natural persons who establish the business relationship for legal entities and partnerships",
          de: "Angaben zu den natürlichen Personen, die für juristische Personen und Personengesellschaften die Geschäftsbeziehung eröffnen (Eröffner)",
        }),
        description: i18n.fixed({
          en: "For legal entities and partnerships the identity of the natural persons who establish the business relationship must be verified.",
          de: "Bei juristischen Personen und Personengesellschaften ist die Identität der natürlichen Personen, welche die Geschäftsbeziehung eröffnen, zu überprüfen.",
        }),
        hide(root) {
          return root[TalerFormAttributes.CUSTOMER_TYPE] !== "LEGAL_ENTITY";
        },
        fields: [
          {
            id: TalerFormAttributes.ESTABLISHER_LIST,
            label: i18n.fixed({
              en: "Persons establishing the legal relationship",
              de: "Personen, welche die Geschäftsbeziehung begründen",
            }),
            type: "array",
            labelFieldId: TalerFormAttributes.FULL_NAME,
            required: true,
            fields: [
              {
                id: TalerFormAttributes.FULL_NAME,
                label: i18n.fixed({
                  en: "Full name",
                  de: "Vollständiger Name",
                }),
                type: "text",
                required: true,
              },
              {
                id: TalerFormAttributes.DOMICILE_ADDRESS,
                label: i18n.fixed({
                  en: "Residential address",
                  de: "Wohnsitzadresse",
                }),
                type: "textArea",
                required: true,
              },
              {
                id: TalerFormAttributes.DATE_OF_BIRTH,
                label: i18n.fixed({ en: "Date of birth", de: "Geburtsdatum" }),
                type: "isoDateText",
                placeholder: "dd/MM/yyyy",
                pattern: "dd/MM/yyyy",
                defaultCalendarValue: "1980-01-01",
                required: true,
                validator(text, form) {
                  //FIXME: why returning in this format even if pattern is in another?
                  const time = parse(text, "yyyy-MM-dd", new Date());
                  if (!isValid(time)) {
                    return i18n.fixed({
                      en: "invalid format",
                      de: "ungültiges Format",
                    });
                  }
                  if (isFuture(time)) {
                    return i18n.fixed({
                      en: "it can't be in the future",
                      de: "darf nicht in der Zukunft liegen",
                    });
                  }
                  const { years } = intervalToDuration({
                    start: time,
                    end: new Date(),
                  });
                  if (years && years > 120) {
                    return i18n.fixed({
                      en: "it can't be greater than 120 years",
                      de: "darf nicht größer als 120 Jahre sein",
                    });
                  }
                  return undefined;
                },
              },
              {
                id: TalerFormAttributes.NATIONALITY,
                label: i18n.fixed({
                  en: "Nationality",
                  de: "Staatsangehörigkeit",
                }),
                type: "selectOne",
                choices: countryNationalityList(i18n),
                preferredChoiceVals: ["CH"],
                required: true,
              },
              {
                id: TalerFormAttributes.PERSONAL_IDENTIFICATION_DOCUMENT_COPY,
                label: i18n.fixed({
                  en: "Copy of identification document",
                  de: "Kopie des Ausweisdokuments",
                }),
                type: "file",
                accept: "application/pdf",
                tooltip: i18n.fixed({
                  en: "Only official government IDs (incl. passports) are accepted. Please scan both sides if applicable.",
                  de: "Es werden nur amtliche Ausweise (inkl. Pässe) akzeptiert. Bitte scannen Sie gegebenenfalls beide Seiten.",
                }),
                required: true,
              },
              {
                id: TalerFormAttributes.SIGNING_AUTHORITY_TYPE,
                tooltip: i18n.fixed({
                  en: "Signing authority of the person",
                  de: "Zeichnungsberechtigung der Person",
                }),
                label: i18n.fixed({
                  en: "Power of attorney arrangements",
                  de: "Vollmachtregelung",
                }),
                type: "choiceStacked",
                required: true,
                choices: [
                  {
                    label: i18n.fixed({
                      en: "Sole signature authority",
                      de: "Einzelzeichnungsberechtigung",
                    }),
                    value: "SINGLE",
                  },
                  {
                    label: i18n.fixed({
                      en: "Collective authority with two signatures",
                      de: "Kollektivzeichnungsberechtigung zu zweien",
                    }),
                    value: "COLLECTIVE_TWO",
                  },
                  {
                    label: i18n.fixed({
                      en: "Other (please specify)",
                      de: "Andere (bitte angeben)",
                    }),
                    value: "OTHER",
                  },
                ],
              },
              {
                id: TalerFormAttributes.SIGNING_AUTHORITY_TYPE_OTHER,
                required: true,
                label: i18n.fixed({
                  en: "Other type of signing authority",
                  de: "Andere Art der Zeichnungsberechtigung",
                }),
                type: "text",
                hide(value, root) {
                  return (
                    root[TalerFormAttributes.SIGNING_AUTHORITY_TYPE] !== "OTHER"
                  );
                },
              },
              {
                id: TalerFormAttributes.SIGNING_AUTHORITY_EVIDENCE,
                required: true,
                label: i18n.fixed({
                  en: "Evidence of signing authority:",
                  de: "Nachweis der Zeichnungsberechtigung:",
                }),
                type: "choiceStacked",
                choices: [
                  {
                    value: "CR",
                    label: i18n.fixed({
                      en: "Company register extract",
                      de: "Handelsregisterauszug",
                    }),
                  },
                  {
                    value: "MANDATE",
                    label: i18n.fixed({ en: "Mandate", de: "Mandat" }),
                  },
                  {
                    value: "OTHER",
                    label: i18n.fixed({ en: "Other", de: "Andere" }),
                  },
                ],
              },
              {
                id: TalerFormAttributes.SIGNING_AUTHORITY_EVIDENCE_OTHER,
                required: true,
                label: i18n.fixed({
                  en: "Specify other way of establishing signing authority:",
                  de: "Andere Art des Nachweises der Zeichnungsberechtigung angeben:",
                }),
                type: "text",
                hide(value, root) {
                  return (
                    root[TalerFormAttributes.SIGNING_AUTHORITY_EVIDENCE] !==
                    "OTHER"
                  );
                },
              },
              {
                id: TalerFormAttributes.SIGNING_AUTHORITY_EVIDENCE_DOCUMENT_COPY,
                label: i18n.fixed({
                  en: "Copy of document that serves as evidence of signing authority:",
                  de: "Kopie des Dokuments, das als Nachweis der Zeichnungsberechtigung dient:",
                }),
                type: "file",
                accept: "application/pdf",
                required: true,
              },
            ],
          },
        ],
      },
      // Version of the question for natural persons
      {
        title: i18n.fixed({
          en: "Customer classification",
          de: "Kundenklassifizierung",
        }),
        description: i18n.fixed({
          en: "Classification for the establishment of the beneficial owner of the assets and/or controlling person",
          de: "Klassifizierung zur Feststellung des wirtschaftlich Berechtigten der Vermögenswerte und/oder des Kontrollinhabers",
        }),
        hide(root) {
          return root[TalerFormAttributes.CUSTOMER_TYPE] !== "NATURAL_PERSON";
        },
        fields: [
          {
            id: TalerFormAttributes.CUSTOMER_TYPE_VQF,
            required: true,
            label: i18n.fixed({ en: "The customer is:", de: "Der Kunde ist:" }),
            type: "choiceStacked",
            choices: [
              {
                value: "NATURAL_PERSON",
                label: i18n.fixed({
                  en: "A natural person and there are no doubts that this person is the sole beneficial owner of the assets",
                  de: "Eine natürliche Person und es bestehen keine Zweifel, dass diese selber an den Vermögenswerten wirtschaftlich berechtigt ist",
                }),
              },
              {
                value: "OTHER",
                label: i18n.fixed({ en: "Other", de: "Andere" }),
              },
            ],
          },
        ],
      },
      // Version for Businesses
      {
        title: i18n.fixed({
          en: "Customer classification",
          de: "Kundenklassifizierung",
        }),
        description: i18n.fixed({
          en: "Classification for the establishment of the beneficial owner of the assets and/or controlling person",
          de: "Klassifizierung zur Feststellung des wirtschaftlich Berechtigten der Vermögenswerte und/oder des Kontrollinhabers",
        }),
        hide(root) {
          return root[TalerFormAttributes.CUSTOMER_TYPE] !== "LEGAL_ENTITY";
        },
        fields: [
          {
            id: TalerFormAttributes.CUSTOMER_TYPE_VQF,
            required: true,
            label: i18n.fixed({ en: "The customer is:", de: "Der Kunde ist:" }),
            type: "choiceStacked",
            choices: [
              {
                value: "OPERATIONAL",
                label: i18n.fixed({
                  en: "An operational legal entity or partnership",
                  de: "Eine operative juristische Person oder Personengesellschaft",
                }),
              },
              {
                value: "FOUNDATION",
                label: i18n.fixed({
                  en: "A foundation (or a similar construct; incl. underlying companies)",
                  de: "Eine Stiftung (oder ein ähnliches Konstrukt; inkl. Underlying Companies)",
                }),
              },
              {
                value: "TRUST",
                label: i18n.fixed({
                  en: "A trust (incl. underlying companies)",
                  de: "Ein Trust (inkl. Underlying Companies)",
                }),
              },
              {
                value: "LIFE_INSURANCE",
                label: i18n.fixed({
                  en: "A life insurance policy with separately managed accounts / securities accounts (so-called insurance wrappers)",
                  de: "Eine Lebensversicherung mit separater Konto-/Depotführung (sog. Insurance Wrapper)",
                }),
              },
              {
                value: "OTHER",
                label: i18n.fixed({ en: "Other", de: "Andere" }),
              },
            ],
          },
        ],
      },
      {
        title: i18n.fixed({ en: "Signature(s)", de: "Unterschrift(en)" }),
        description: i18n.fixed({
          en: "It is a criminal offence to deliberately provide false information on this form (article 251 of the Swiss Criminal Code, document forgery)",
          de: "Die vorsätzliche Angabe falscher Informationen in diesem Formular ist eine strafbare Handlung (Urkundenfälschung gemäss Artikel 251 des Schweizerischen Strafgesetzbuchs).",
        }),
        fields: [
          {
            type: "caption",
            label: i18n.fixed({
              en: "The contracting partner hereby undertakes to inform automatically of any changes to the information contained herein.",
              de: "Der Vertragspartner verpflichtet sich, Änderungen jeweils unaufgefordert mitzuteilen.",
            }),
          },
          {
            id: TalerFormAttributes.SIGNATURE,
            label: i18n.fixed({ en: "Signature(s)", de: "Unterschrift(en)" }),
            type: "text",
            required: true,
          },
          {
            id: TalerFormAttributes.SIGN_DATE,
            label: i18n.fixed({ en: "Date", de: "Datum" }),
            type: "isoDateText",
            defaultValue: today,
            placeholder: "dd/MM/yyyy",
            disabled: true,
            pattern: "dd/MM/yyyy",
            required: true,
          },
        ],
      },
    ],
  };
}
